import { graphql } from 'gatsby';
import Img from 'gatsby-image';

import IndexLayout from '../layouts';
import LazyLoad from 'react-lazyload';
import Wrapper from '../components/Wrapper';
import SiteNav from '../components/header/SiteNav';
import BackToTop from '../components/BackToTop';
import { SiteHeader, outer, inner, SiteMain } from '../styles/shared';
import * as React from 'react';
import { css } from '@emotion/core';
import { PostFullHeader, PostFullTitle, NoImage, PostFull } from '../templates/post';
import { PostFullContent } from '../components/PostContent';
import Footer from '../components/Footer';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';

import ButtonLink from '../../src/components/ui/ButtonLink';
import ReactCompareImage from 'react-compare-image';
import Spinner from '../../src/components/ui/Spinner';

//import ShareButtons from '../components/ShareButtons';
import MoreGoodies from '../components/more-goodies/MoreGoodies';

export const query = graphql`
  query {
    file(relativePath: { eq: "covers/free-luts-cover-o.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1800) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`;

interface CompareWrapperProps {
  thin?: boolean;
}

const CompareWrapper = styled.div<CompareWrapperProps>`
  max-width: ${(props) => (props.thin ? '450px' : '100%')};
  margin: 0 auto 3rem;
`;

class RerenderCompare extends React.Component {
  t1;
  state = { delayOver: false };

  componentDidMount() {
    this.t1 = setTimeout(() => {
      this.setState({ delayOver: true });
    }, 500);
  }

  componentWillUnmount() {
    clearTimeout(this.t1);
  }

  render() {
    if (!this.state.delayOver) {
      return <Spinner />;
    }
    return this.props.children();
  }
}

const PageTemplate = css`
  .header-image {
    margin-bottom: 4.5rem;
    box-shadow: 0 10px 20px -16px #382757;
  }

  .site-main {
    background #fff;
    padding-bottom: 4vw;
  }

  .post-content {
    max-width: 1000px;
    margin: 0 auto;
  }
  .post-content .large-key .key {
    font-size: 3rem;
    padding: 15px;
    margin-top: 20px;
    display: inline-block;
    border-radius: 6px;
  }
  .large-key {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 4rem;
  }

  @media(max-width: 400px) {
    .large-key {
      grid-template-columns: 1fr 1fr;
    }
  }

  h2::before {
    display: none !important;
  }
`;

const FreeLuts: React.FunctionComponent = ({ data }) => {
  const [thanks, setThanks] = React.useState(false);

  function thankYouMessage() {
    setThanks(true);
  }

  return (
    <IndexLayout>
      <Helmet>
        <title>Pack of 12 Free LUTs for Photoshop CC - Color Grading</title>
        <meta
          name="description"
          content="A pack of 12 truly free LUTs for creative color grading in Photoshop CC with instructions on how to install. Change the look of your photos in a few clicks!"
        />

        <meta property="og:site_name" content="Giggster Guide" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="12 Free LUTs for Photoshop CC" />
        <meta
          property="og:description"
          content="A collection of 12 free LUTs for creative color grading in Photoshop CC. Change the look of your images in just a few clicks!"
        />
        <meta property="og:url" content="https://giggster.com/guide/free-luts/" />
        <meta property="og:image" content="https://giggster.com/guide/covers/free-luts.jpg" />
        <meta property="article:publisher" content="https://www.facebook.com/giggsterHQ" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="12 Free LUTs for Photoshop CC" />
        <meta
          name="twitter:description"
          content="A collection of 12 free LUTs for creative color grading in Photoshop CC. Change the look of your images in just a few clicks!"
        />
        <meta name="twitter:url" content="https://giggster.com/guide/free-luts/" />
        <meta name="twitter:image" content="https://giggster.com/guide/covers/free-luts.jpg" />
        <meta name="twitter:site" content="@Giggster" />
        <meta name="twitter:creator" content="@Giggster" />
      </Helmet>
      <Wrapper css={PageTemplate}>
        <header css={[outer, SiteHeader]}>
          <div css={inner}>
            <SiteNav />
          </div>
        </header>
        <main id="site-main" className="site-main" css={[SiteMain, outer]}>
          <article className="post page" css={[PostFull, NoImage]}>
            <PostFullHeader>
              <PostFullTitle>✨ Free LUTs for Photoshop</PostFullTitle>
              <h2 className="b3">
                🎩 A collection of 12 totally free LUTs to use for color grading and creative
                effects
              </h2>
              <Img
                className="header-image"
                fluid={data.file.childImageSharp.fluid}
                alt="Cover: Example images retouched with free LUTs"
              />
              {/*<ShareButtons
                title={'Free LUTs for Photoshop CC'}
                slug={'/free-luts/'}
                imgSrc={'covers/free-luts.jpg'}
              />*/}
            </PostFullHeader>

            <PostFullContent className="post-full-content">
              <div className="post-content">
                <p>
                  Look up tables (known as <strong>LUTs</strong>) are files that embed instructions
                  to change the hue, saturation or luminosity of pixels in an image or for video
                  footage according to predefined rules.
                </p>
                <p>
                  I've covered how to{' '}
                  <a href="/guide/retouching/luts/">create and use LUTs in Photoshop in this post</a>, and
                  here I wanted to provide a pack of free LUTs that you can start using right away.
                </p>
                <p>
                  LUTs are used very often to color grade or add color effects to video footage. The
                  LUTs in this free pack will work just as well in video editing software like
                  Premiere Pro or DaVinci Resolve, but they are crafted to work especially well for
                  Photoshop.
                </p>
                <h2 className="text-center">&darr;</h2>
                <p className="text-center">
                  <ButtonLink
                    href="https://s3.amazonaws.com/purple11/free-luts/purple11-free-luts-pack.zip"
                    download
                    onClick={thankYouMessage}
                  >
                    Download this free LUTs pack
                  </ButtonLink>
                </p>
                {thanks ? (
                  <div>
                    <section
                      className="info-box text-center hidden"
                      style={{ border: '#F2EAF8 5px solid' }}
                    >
                      <p className="text-center t3">
                        Thank you for downloading! 🎉 Never stop creating! 📷
                      </p>
                    </section>
                    <hr />
                  </div>
                ) : (
                  ''
                )}
                <p className="text-center">
                  <small>
                    <a href="#instructions">Installation instructions</a>
                  </small>
                </p>
               
                <hr />
                <h2 className="text-center">12 Free Photoshop LUTs</h2>
                <p>
                  Here are the 12 free LUTs included in the pack. Drag the slider on the image to
                  see the before and after:
                </p>
                <h3 className="text-center">Passing By</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/free-luts/passing-by-before.jpg"
                          rightImage="/guide/images/free-luts/passing-by-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  A very warm look with desaturated cool colors and oversaturated warm colors. Great
                  for summer and outdoor photography.
                </p>
                <hr />
                <h3 className="text-center">Serenity</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/free-luts/serenity-before.jpg"
                          rightImage="/guide/images/free-luts/serenity-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">A pop of contrast and very vibrant colors.</p>
                <hr />
                <h3 className="text-center">Urban Cowboy</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/free-luts/urban-cowboy-before.jpg"
                          rightImage="/guide/images/free-luts/urban-cowboy-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  Warm tones throughout and added contrast, for a very western and warm feel.
                </p>
                <hr />
                <h3 className="text-center">We'll See</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/free-luts/well-see-before.jpg"
                          rightImage="/guide/images/free-luts/well-see-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  The very popular teal/orange look often seen in movies where the shadows have a
                  slightly teal tone and the skin tone are a little bit more orange.
                </p>
                <hr />
                <h3 className="text-center">Undeniable</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={true}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/free-luts/undeniable-before.jpg"
                          rightImage="/guide/images/free-luts/undeniable-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  A high-contrast, desaturated look with a slight bleach bypass effect.
                </p>
                <hr />
                <h3 className="text-center">Undeniable 2</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={true}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/free-luts/undeniable-before.jpg"
                          rightImage="/guide/images/free-luts/undeniable-2-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  Very similar to <em>Undeniable</em>, but in black and white instead of color.
                </p>
                <hr />
                <h3 className="text-center">You Can Do It</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={true}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/free-luts/you-can-do-it-before.jpg"
                          rightImage="/guide/images/free-luts/you-can-do-it-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  Exaggerated contrast, lower dark point, punchy colors, and blues changed to teal.
                  Perfect for many landscape and outdoor images.
                </p>
                <hr />
                <h3 className="text-center">Nah</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={true}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/free-luts/nah-before.jpg"
                          rightImage="/guide/images/free-luts/nah-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  Extra contrast, lower dark point, blended with a blue/orange color grade.
                </p>
                <hr />
                <h3 className="text-center">Smooth Sailing</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={true}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/free-luts/smooth-sailing-before.jpg"
                          rightImage="/guide/images/free-luts/smooth-sailing-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">A warm, yet highly desaturated look.</p>
                <hr />
                <h3 className="text-center">Going for a Walk</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/free-luts/going-for-a-walk-before.jpg"
                          rightImage="/guide/images/free-luts/going-for-a-walk-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">High-contrast and exaggerated blues.</p>
                <hr />
                <h3 className="text-center">Good Morning</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/free-luts/good-morning-before.jpg"
                          rightImage="/guide/images/free-luts/good-morning-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">Warm with bright and contrasty colors.</p>
                <hr />
                <h3 className="text-center">Once Upon a Time</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/free-luts/once-upon-a-time-before.jpg"
                          rightImage="/guide/images/free-luts/once-upon-a-time-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  Magenta tones in the shadows and high contrast, for a vintage feel.
                </p>
                <hr />
                <h2 id="instructions">Photoshop LUTs Installation and Usage Instruction</h2>
                Below you'll find instructions on how to install and use the LUTs from this free
                pack inside of Photoshop CC.
                <h3>Installation</h3>
                <p>
                  Uncompress the <em>.zip</em> file, copy the extracted <em>.CUBE</em> files inside
                  Photoshop's 3DLuts folder. On a Mac you'll find that folder at{' '}
                  <strong>Applications > Photoshop CC 2019 > Presets > 3DLuts</strong>. On Windows,
                  the folder is under{' '}
                  <strong>Program Files > Photoshop CC 2019 > Presets > 3DLuts</strong>.
                </p>
                <h3>Usage</h3>
                <p>
                  Add a Color Lookup adjustment layer to your image, and in the{' '}
                  <strong>3DLUT File</strong> dropdown you'll see the LUTs that you installed.
                  Remember that you can lower the opacity of the adjustment layer to reduce the
                  effect, or you can duplicate the adjustment layer to increase the effect.
                </p>
              </div>
            </PostFullContent>
          </article>
          <MoreGoodies slug="free-luts" />
        </main>
        <BackToTop />
        <Footer />
      </Wrapper>
    </IndexLayout>
  );
};

export default FreeLuts;
